import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledMentionsInput from 'components/molecules/Mentions/MentionsInput/MentionsInput.styles';
import Mention from 'components/molecules/Mentions/Mention/Mention';
import MentionList from 'components/molecules/Mentions/MentionList/MentionList';

const MentionsInput = React.forwardRef((props, ref) => {
  const {
    size,
    mentions,
    multiline,
    ...innerProps
  } = useComponentProps({
    color: 'primary',
    ...props
  }, 'MentionsInput', {
    styled: ['color', 'rows', 'minRows', 'maxRows']
  });

  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const mentionsMemo = useMemo(() => {
    return mentions.map((mention) => ({
      ...mention,
      Component: mention.Component ?? Mention,
      markup: `${mention.trigger}[${mention.trigger}__display__:__id__]`,
      displayTransform: (id, display) => {
        return `${mention.trigger}${display}`
      }
    }))
  }, [mentions]);

  const testText = useCallback((text) => {
    let canvas = innerRef.current.parentElement.querySelector('.text-test');
    if (!canvas) {
      canvas = document.createElement('span');
      canvas.className = 'text-test';
      innerRef.current.parentElement.appendChild(canvas);
    }

    if (canvas.innerHTML !== text) {
      canvas.innerHTML = text;
    }

    return {
      width: canvas.clientWidth,
      height: canvas.clientHeight
    }
  }, []);

  innerProps.style = utils.mergeObjects({
    input: {
      overflow: multiline ? 'auto' : 'hidden',
    },
    highlighter: {
      border: 'unset'
    },
  }, innerProps.style);

  const renderSuggestions = (children) => {
    if (innerRef) {
      const nodes = children.props.children;
      const query = nodes[0]?.props?.query ?? '';

      const findCaret = (el) => {
        if (el.nodeType === Node.TEXT_NODE && el.textContent.endsWith(`@${query}`)) {
          return el;
        } else {
          let found;
          Array.from(el.childNodes ?? []).some((el) => {
            found = findCaret(el);
            return found;
          })
          return found;
        }
      }
      const anchorEl = findCaret(innerRef.current.parentElement.children[0]);

      if (anchorEl) {
        let bBox;

        const offset = {x: 0, y: 0};
        const text = anchorEl.textContent;
        const atIndex = text.lastIndexOf(`@${query}`);
        try {
          const textStats = testText('@');
          const range = document.createRange();
          range.setStart(anchorEl, atIndex);
          range.setEnd(anchorEl, atIndex + 1);
          bBox = Array.from(range.getClientRects())
            .filter((cr) => Math.round(cr.width) >= Math.round(textStats.width))
            .sort((a, b) => a.width - b.width)[0];
        } catch {
          /* SQUASH */
        }

        if (bBox) {
          return <MentionList items={nodes?.map((node) => ({
                                onClick: node.props.onClick,
                                onMouseEnter: node.props.onMouseEnter,
                                selected: node.props.focused,
                                label: node.props.children?.[0] ? utils.cloneElement(node.props.children?.[0]) :
                                  node.props.suggestion.label
                              }))}
                              anchorEl={{
                                getBoundingClientRect: () => {
                                  return new DOMRect(bBox?.left + offset.x,
                                    bBox?.top + offset.y, bBox.width, bBox.height);
                                }
                              }}
                              open={true}
                              autoFocus={false}
                              ContextPopperProps={{
                                size: 'small',
                                density: 'densest',
                                placement: 'top-start'
                              }} />
        }
      }
    }
  }

  const inputEl = innerRef.current;
  useEffect(() => {
    if (inputEl) {
      return utils.observeEvent(inputEl, 'click', () => {
        inputEl.parentElement.children[0].scrollTo({
          top: inputEl.parentElement.children[1].scrollTop,
          left: inputEl.parentElement.children[1].scrollLeft,
          behavior: 'instant'
        });
      });
    }
  }, [inputEl]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledMentionsInput inputRef={innerRef} {...innerProps}
                              customSuggestionsContainer={renderSuggestions}
                              isOpened={false}
                              singleLine={!multiline}>
    {mentionsMemo.map((mention, idx) => {
      return <mention.Component key={idx}
                                regex={mention.regex}
                                markup={mention.markup}
                                displayTransform={mention.displayTransform}
                                trigger={mention.trigger}
                                renderSuggestion={mention.renderSuggestion}
                                data={mention.data}
                                appendSpaceOnAdd={true}
                                isLoading={false}
                                {...mention.MentionProps}/>
    })}
  </StyledMentionsInput>
});

MentionsInput.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.string,
  multiline: PropTypes.bool
};

export default MentionsInput;
